import {
    formatAnswerBy,
    formatAnswerDay,
    PROGRAM_NAMES,
} from '@/shared/utils/deadline';
import { meetingLines, positionCourse } from '@/shared/utils/positionText';
import { OPEN_CANDIDACY_STAGES } from '@/shared/utils/candidacyStage';

const TIME_ZONE = 'America/Denver';
const ANSWERED_STAGES = ['accepted', 'declined', 'released', 'expired'];

/** "CHE 1810, section 003": the short name a row and a dialog use. */
export function positionShortName(position) {
    const course = positionCourse(position);
    const section = position?.section ? `section ${position.section}` : '';
    return [course, section].filter(Boolean).join(', ') || 'A position';
}

const dayOf = (value) =>
    new Date(value).toLocaleDateString('en-US', {
        timeZone: TIME_ZONE,
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });

/** "Thursday, Oct 29, 11:00 AM Mountain time" */
export function formatInterviewWhen(value) {
    if (!value) return '';
    const date = new Date(value);
    const day = date.toLocaleDateString('en-US', {
        timeZone: TIME_ZONE,
        weekday: 'long',
        month: 'short',
        day: 'numeric',
    });
    const time = date
        .toLocaleTimeString('en-US', {
            timeZone: TIME_ZONE,
            hour: 'numeric',
            minute: '2-digit',
        })
        .replace(/\s/g, ' ');
    return `${day}, ${time} Mountain time`;
}

/**
 * The one date that matters on a position row: when the interview is, when an
 * offer must be answered by, or else when the student applied or answered.
 * Null when the candidacy carries none.
 */
export function keyDateOf(candidacy) {
    const { stage, interview, offer } = candidacy;
    if (stage === 'interview' && interview?.when) {
        return `Interview ${formatInterviewWhen(interview.when)}`;
    }
    if (stage === 'offered' && offer?.respondBy) {
        return `Answer by ${formatAnswerBy(offer.respondBy)}`;
    }
    if (stage === 'expired' && offer?.respondBy) {
        return `Expired ${formatAnswerDay(offer.respondBy)}`;
    }
    if (['accepted', 'declined'].includes(stage) && offer?.respondedAt) {
        const verb = stage === 'accepted' ? 'Accepted' : 'Declined';
        return `${verb} ${dayOf(offer.respondedAt)}`;
    }
    if (candidacy.createdAt) return `Applied ${dayOf(candidacy.createdAt)}`;
    return null;
}

const byTime = (get) => (a, b) =>
    new Date(get(a) || 8.64e15) - new Date(get(b) || 8.64e15);

/**
 * What needs the student now: offers by answer date, then interviews by
 * time. A past interview is not a next step.
 */
export function nextSteps(candidacies, now = new Date()) {
    const offers = candidacies
        .filter((c) => c.stage === 'offered')
        .sort(byTime((c) => c.offer?.respondBy));
    const interviews = candidacies
        .filter(
            (c) =>
                c.stage === 'interview' &&
                c.interview?.when &&
                new Date(c.interview.when) >= now
        )
        .sort(byTime((c) => c.interview.when));
    return { offers, interviews };
}

// Stages that are over for the student: nothing more to answer or attend.
const PAST_STAGES = [
    'declined',
    'not_selected',
    'withdrawn',
    'closed',
    'unavailable',
    'released',
];

// Stages with a final outcome: an interview from earlier is history, so the
// row stops offering it
export const DECIDED_STAGES = Object.freeze([
    'accepted',
    'expired',
    ...PAST_STAGES,
]);
const STAGE_ORDER = [
    'offered',
    'interview',
    'accepted',
    'finalist',
    'screening',
    'applied',
    'expired',
];

const rankOf = (stage) => {
    const index = STAGE_ORDER.indexOf(stage);
    return index === -1 ? STAGE_ORDER.length : index;
};

const answerByOf = (candidacy) =>
    candidacy.offer?.respondBy
        ? new Date(candidacy.offer.respondBy).getTime()
        : Number.POSITIVE_INFINITY;

/**
 * The student's positions for the list: `current` with offers first, then
 * interviews, then accepted places, then the rest; `past` for the outcomes
 * that are over (declined, not selected, withdrawn, closed, ended). Offers
 * with the earliest answer-by day lead; otherwise the sort is stable, so each
 * group keeps the server's order.
 */
export function groupPositions(candidacies) {
    const current = candidacies
        .filter((c) => !PAST_STAGES.includes(c.stage))
        .sort(
            (a, b) =>
                rankOf(a.stage) - rankOf(b.stage) ||
                (a.stage === 'offered' ? answerByOf(a) - answerByOf(b) : 0)
        );
    const past = candidacies.filter((c) => PAST_STAGES.includes(c.stage));
    return { current, past };
}

/** "5 positions: 2 offers, 1 interview" for the card header. */
export function positionCountsText(candidacies) {
    const total = candidacies.length;
    if (total === 0) return 'No positions yet';
    const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
    const parts = [];
    const offers = candidacies.filter((c) => c.stage === 'offered').length;
    const interviews = candidacies.filter(
        (c) => c.stage === 'interview'
    ).length;
    if (offers) parts.push(plural(offers, 'offer', 'offers'));
    if (interviews) parts.push(plural(interviews, 'interview', 'interviews'));
    const head = plural(total, 'position', 'positions');
    return parts.length ? `${head}: ${parts.join(', ')}` : head;
}

/**
 * Meeting times for an offer, or the sentence that says they are missing and
 * who to ask.
 */
export function meetingsOrMissing(position) {
    const lines = meetingLines(position?.meetings);
    if (lines.length > 0) return { lines, missing: null };
    const who = position?.instructorName || 'the instructor';
    return {
        lines: [],
        missing: `Meeting times not listed yet; ask ${who}.`,
    };
}

/** The training sessions that apply to this student: new or returning. */
export function sessionsFor(sessions, isReturning) {
    return (sessions || []).filter(({ appliesTo }) => {
        if (appliesTo === 'all') return true;
        if (appliesTo === 'new') return isReturning !== true;
        return isReturning === true;
    });
}

/**
 * The "If you accept" items, in the order a student weighs them. Each is a
 * sentence; nothing is invented that the position, the student's commitment
 * or the program's own dates do not state.
 *
 * @param {object} args
 * @param {object} args.position
 * @param {object} args.picture - hoursPicture of the offer card
 * @param {object} [args.programLimits] - commitment.programs[program]
 * @param {string} args.program
 * @param {object[]} [args.sessions] - program training sessions that apply
 * @param {number} [args.otherOffers] - the student's other open offers
 */
export function acceptItems({
    position,
    picture,
    programLimits,
    program,
    sessions = [],
    otherOffers = 0,
}) {
    const items = [];
    if (picture.hours !== null) {
        items.push(`About ${picture.hours} hours a week.`);
    }
    const { lines, missing } = meetingsOrMissing(position);
    if (lines.length > 0) items.push(`Meets: ${lines.join('; ')}.`);
    else items.push(missing);
    for (const { title, when } of sessions) {
        items.push(`${title}: ${when}.`);
    }
    if (picture.total !== null) {
        items.push(
            `This brings you to ${picture.total} hours a week; the university limit is ${picture.limit}.`
        );
    }
    const cap = programLimits?.maxPositionsPerStudent;
    if (cap !== null && cap !== undefined) {
        const name = PROGRAM_NAMES[program] || program?.toUpperCase();
        items.push(
            `You can hold at most ${cap} ${name} ${cap === 1 ? 'position' : 'positions'} in a term.`
        );
    }
    if (otherOffers > 0) {
        items.push(
            `Your ${otherOffers === 1 ? 'other offer stays' : `${otherOffers} other offers stay`} open until you answer ${otherOffers === 1 ? 'it' : 'them'}. Each is checked against the limits when you accept it.`
        );
    }
    return items;
}

const pad = (n) => String(n).padStart(2, '0');
const icsStamp = (date) =>
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
const icsText = (value) =>
    String(value)
        .replace(/\\/g, '\\\\')
        .replace(/\r?\n/g, '\\n')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,');

/**
 * An iCalendar event for the interview, one hour long (the program sets no
 * length). Null when the interview has no time.
 */
export function interviewIcs(candidacy, { now = new Date() } = {}) {
    const { interview, position } = candidacy;
    if (!interview?.when) return null;
    const start = new Date(interview.when);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    const lines = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Peer Support Hub//Interview//EN',
        'CALSCALE:GREGORIAN',
        'BEGIN:VEVENT',
        `UID:interview-${candidacy.id}@peer-support-hub`,
        `DTSTAMP:${icsStamp(now)}`,
        `DTSTART:${icsStamp(start)}`,
        `DTEND:${icsStamp(end)}`,
        `SUMMARY:${icsText(`Interview: ${positionShortName(position)}`)}`,
    ];
    if (interview.where) lines.push(`LOCATION:${icsText(interview.where)}`);
    if (interview.details) {
        lines.push(`DESCRIPTION:${icsText(interview.details)}`);
    }
    lines.push('END:VEVENT', 'END:VCALENDAR');
    return `${lines.join('\r\n')}\r\n`;
}

const UNLISTED_NAME = 'a position that is no longer listed';
const nameOf = (candidacy) =>
    candidacy.position ? positionShortName(candidacy.position) : UNLISTED_NAME;

/** The positions a withdrawn application takes with it, and the ones it keeps. */
export function withdrawEffects(candidacies) {
    return {
        affected: candidacies
            .filter((c) => OPEN_CANDIDACY_STAGES.includes(c.stage))
            .map(nameOf),
        kept: candidacies.filter((c) => c.stage === 'accepted').map(nameOf),
    };
}

/** Whether a stage is one the student has answered or that has ended. */
export const isAnsweredStage = (stage) => ANSWERED_STAGES.includes(stage);

/** The stages a student sees, in the student's words, by candidacy stage. */
export const STAGE_LEGEND = Object.freeze([
    [
        'applied',
        'The program has your application and has not looked at it yet.',
    ],
    ['screening', 'Reviewers are reading applications for this position.'],
    [
        'interview',
        'The instructor wants to meet you. The time and place are on the row.',
    ],
    ['finalist', 'You are one of the last few students being considered.'],
    ['offered', 'You have an offer. Accept or decline by the date shown.'],
    [
        'accepted',
        'You have the position. Your assignment page has what comes next.',
    ],
    ['not_selected', 'The position went to someone else this time.'],
]);
